import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import {
  Chip,
  CircularProgress,
  IconButton,
} from '@mui/material';

import { PrimaryButton } from '../../../../../../components/Button/ActionButtons';
import { StyledTextField } from '../../../../../../components/Inputs';
import InputLabel from '../../../../../../components/v2/InputLabel';
import TagsSelector from '../../../../../../components/v2/MultiSelectAutoComplete';
import { WarningTag } from '../../../../../../components/Tags';
import { CardStatus, UploadStatus, SaveStatus } from '../constants';
import { defaultCardName } from '../utils';
import texts from '../texts';
import {
  CheckIcon,
  ChevronDownIcon,
  TrashIcon,
  WarnIcon,
} from '../icons';
import {
  Card,
  CardHead,
  Chevron,
  HeadNum,
  HeadTitle,
  Grow,
  CardBody,
  VideoCol,
  FieldsCol,
  FieldLabel,
  TagList,
  SavedRow,
  SavedCheck,
  SavedNum,
  SavedName,
  SavedNote,
  SaveFailBar,
} from '../styles';
import VideoColumn from './VideoColumn';

// The chip icon slot renders the loader flush against the chip's edge — give
// it breathing room from the label/border.
const spinner = <CircularProgress size={12} color="inherit" sx={{ marginLeft: '8px' }} />;
const listboxStyle = { maxHeight: '150px' };
const tagShape = PropTypes.shape({ id: PropTypes.string, label: PropTypes.string });

// Card status → MUI Chip. Spinners use CircularProgress so no custom animation.
const renderStatusChip = (status, needReason, progress) => {
  switch (status) {
    case CardStatus.QUEUED:
      return <Chip size="small" color="info" variant="outlined" label={texts.queuedBadge} />;
    case CardStatus.UPLOADING:
      return (
        <Chip
          size="small"
          color="info"
          variant="outlined"
          icon={spinner}
          label={format(texts.uploadingBadge, { progress: progress || 0 })}
        />
      );
    case CardStatus.READY:
      return <Chip size="small" color="success" label={texts.readyBadge} />;
    case CardStatus.NEED:
      return <Chip size="small" color="warning" variant="outlined" label={needReason} />;
    case CardStatus.UPLOAD_FAILED:
      return <Chip size="small" color="error" label={texts.uploadFailedBadge} />;
    case CardStatus.SAVING:
      return <Chip size="small" color="info" icon={spinner} label={texts.savingBadge} />;
    case CardStatus.SAVE_FAILED:
      return <Chip size="small" color="error" label={texts.saveFailedBadge} />;
    case CardStatus.SAVED:
      return <Chip size="small" color="success" label={texts.savedBadge} />;
    default:
      return null;
  }
};

const ExerciseCard = ({
  card,
  number,
  view,
  tagOptions,
  onFieldChange,
  onTagsChange,
  onToggleCollapse,
  onSave,
  onRemove,
  onRetrySave,
  onRetryUpload,
  onRemoveVideo,
  onAddVideo,
  onEdit,
}) => {
  const {
    status, nameError, ready, needReason,
  } = view;
  const isSaved = status === CardStatus.SAVED;
  const trimmedName = card.name.trim();

  // Saved done-row (collapsed) — the terminal green row.
  if (isSaved && card.collapsed !== false) {
    const note = card.upload && card.upload.status === UploadStatus.UPLOADED
      ? texts.savedWithVideoNote
      : texts.savedWithoutVideoNote;
    return (
      <Card $saved>
        <SavedRow>
          <SavedCheck><CheckIcon /></SavedCheck>
          <SavedNum>{`#${number}`}</SavedNum>
          <SavedName>{trimmedName || defaultCardName(number)}</SavedName>
          {renderStatusChip(CardStatus.SAVED)}
          <Grow />
          <SavedNote>{note}</SavedNote>
          <PrimaryButton size="small" variant="light" onClick={() => onEdit(card.id)}>
            {texts.edit}
          </PrimaryButton>
        </SavedRow>
      </Card>
    );
  }

  const collapsedDraft = card.collapsed === true && !isSaved;
  const isSaving = card.save === SaveStatus.SAVING;
  // Removal mid-upload is allowed (the upload is aborted); only block it while
  // a Firestore save is in flight.
  const removeDisabled = isSaving;
  // Surface a duplicate/invalid name as soon as the file is selected — the
  // upload chip would otherwise mask it until the upload finishes.
  const showEarlyNameError = !!nameError
    && (status === CardStatus.QUEUED || status === CardStatus.UPLOADING);

  const head = (
    <CardHead>
      <IconButton
        size="small"
        onClick={() => onToggleCollapse(card.id)}
        aria-label={collapsedDraft ? texts.expand : texts.collapse}
      >
        <Chevron $collapsed={collapsedDraft}>
          <ChevronDownIcon />
        </Chevron>
      </IconButton>
      <HeadNum>{number}</HeadNum>
      <HeadTitle $muted={!trimmedName}>
        {trimmedName || texts.untitled}
      </HeadTitle>
      <Grow />
      {showEarlyNameError && (
        <Chip size="small" color="error" variant="outlined" label={texts.fixName} />
      )}
      {renderStatusChip(status, needReason, card.upload ? card.upload.progress : 0)}
      {!isSaved && status !== CardStatus.SAVING && (
        <PrimaryButton size="small" disabled={!ready} onClick={() => onSave(card.id)}>
          {texts.save}
        </PrimaryButton>
      )}
      {isSaved && (
        <PrimaryButton size="small" onClick={() => onSave(card.id)}>
          {texts.update}
        </PrimaryButton>
      )}
      {!isSaved && (
        <IconButton
          size="small"
          color="error"
          aria-label={texts.removeExercise}
          disabled={removeDisabled}
          onClick={() => onRemove(card.id)}
        >
          <TrashIcon />
        </IconButton>
      )}
    </CardHead>
  );

  if (collapsedDraft) {
    return (
      <Card $error={!!nameError} $saveFailed={status === CardStatus.SAVE_FAILED}>
        {head}
      </Card>
    );
  }

  return (
    <Card
      $entering={card.justEntered}
      $error={!!nameError}
      $saveFailed={status === CardStatus.SAVE_FAILED}
      $saved={isSaved}
    >
      {head}
      <CardBody>
        <VideoCol>
          <FieldLabel>{texts.videoLabel}</FieldLabel>
          <VideoColumn
            cardId={card.id}
            upload={card.upload}
            previewUrl={card.previewUrl}
            remoteVideoUrl={card.remoteVideoUrl}
            fileName={card.fileName || ''}
            onRetryUpload={onRetryUpload}
            onRemoveVideo={onRemoveVideo}
            onAddVideo={onAddVideo}
          />
        </VideoCol>
        <FieldsCol>
          <div>
            <InputLabel>{texts.nameLabel}</InputLabel>
            <StyledTextField
              fullWidth
              disabled={isSaving}
              value={card.name}
              placeholder={texts.namePlaceholder}
              error={!!nameError}
              helperText={nameError || ''}
              onChange={(event) => onFieldChange(card.id, 'name', event.target.value)}
            />
          </div>
          <div>
            <InputLabel>{texts.descriptionLabel}</InputLabel>
            <StyledTextField
              fullWidth
              multiline
              rows={3}
              disabled={isSaving}
              value={card.description}
              placeholder={texts.descriptionPlaceholder}
              onChange={(event) => onFieldChange(card.id, 'description', event.target.value)}
            />
          </div>
          <div>
            <InputLabel>{texts.tagsLabel}</InputLabel>
            <TagsSelector
              tagOptions={tagOptions}
              initialValues={card.tags}
              disabled={isSaving}
              onChange={(options) => onTagsChange(card.id, options)}
              listboxStyle={listboxStyle}
            />
            {!!card.tags.length && (
              <TagList>
                {card.tags.map(({ id, label }) => (
                  <WarningTag key={id}>{label}</WarningTag>
                ))}
              </TagList>
            )}
          </div>
        </FieldsCol>
      </CardBody>
      {status === CardStatus.SAVE_FAILED && (
        <SaveFailBar>
          <WarnIcon />
          <Grow>{card.saveError || texts.saveFailedGeneric}</Grow>
          <PrimaryButton size="small" variant="danger" onClick={() => onRetrySave(card.id)}>
            {texts.retrySave}
          </PrimaryButton>
        </SaveFailBar>
      )}
    </Card>
  );
};

ExerciseCard.propTypes = {
  card: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    tags: PropTypes.arrayOf(tagShape),
    collapsed: PropTypes.bool,
    justEntered: PropTypes.bool,
    save: PropTypes.string,
    saveError: PropTypes.string,
    file: PropTypes.instanceOf(File),
    fileName: PropTypes.string,
    previewUrl: PropTypes.string,
    remoteVideoUrl: PropTypes.string,
    upload: PropTypes.shape({
      status: PropTypes.string,
      progress: PropTypes.number,
      error: PropTypes.string,
    }),
  }).isRequired,
  number: PropTypes.number.isRequired,
  view: PropTypes.shape({
    status: PropTypes.string,
    nameError: PropTypes.string,
    ready: PropTypes.bool,
    needReason: PropTypes.string,
  }).isRequired,
  tagOptions: PropTypes.arrayOf(tagShape).isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onTagsChange: PropTypes.func.isRequired,
  onToggleCollapse: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRetrySave: PropTypes.func.isRequired,
  onRetryUpload: PropTypes.func.isRequired,
  onRemoveVideo: PropTypes.func.isRequired,
  onAddVideo: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

export default ExerciseCard;
